/**
 * Application configuration
 */

export interface Operator {
  id: string;
  name: string;
  region: string;
  center: [number, number]; // [longitude, latitude]
  zoom: number;
  hasRealtime: boolean;
  hasVehiclePositions: boolean;
}

export const OPERATORS: Record<string, Operator> = {
  sl: {
    id: 'sl',
    name: 'SL',
    region: 'Stockholm',
    center: [18.0686, 59.3293],
    zoom: 11,
    hasRealtime: true,
    hasVehiclePositions: true
  },
  ul: {
    id: 'ul',
    name: 'UL',
    region: 'Uppsala',
    center: [17.6389, 59.8586],
    zoom: 11,
    hasRealtime: true,
    hasVehiclePositions: true
  },
  otraf: {
    id: 'otraf',
    name: 'Östgötatrafiken',
    region: 'Östergötland',
    center: [15.6214, 58.4108], // Linköping
    zoom: 10,
    hasRealtime: true,
    hasVehiclePositions: true
  },
  jlt: {
    id: 'jlt',
    name: 'JLT',
    region: 'Jönköping',
    center: [14.1618, 57.7826],
    zoom: 10,
    hasRealtime: true,
    hasVehiclePositions: true
  },
  skane: {
    id: 'skane',
    name: 'Skånetrafiken',
    region: 'Skåne',
    center: [13.0038, 55.6050], // Malmö
    zoom: 10,
    hasRealtime: true,
    hasVehiclePositions: true
  },
  vt: {
    id: 'vt',
    name: 'Västtrafik',
    region: 'Västra Götaland',
    center: [11.9746, 57.7089], // Gothenburg
    zoom: 10,
    hasRealtime: true,
    hasVehiclePositions: false // Has realtime but not vehicle positions
  },
  dt: {
    id: 'dt',
    name: 'Dalatrafik',
    region: 'Dalarna',
    center: [15.6356, 60.6749], // Falun
    zoom: 9,
    hasRealtime: true,
    hasVehiclePositions: true
  },
  xt: {
    id: 'xt',
    name: 'X-trafik',
    region: 'Gävleborg',
    center: [17.1410, 60.6749], // Gävle
    zoom: 9,
    hasRealtime: true,
    hasVehiclePositions: true
  },
  sormland: {
    id: 'sormland',
    name: 'Sörmlandstrafiken',
    region: 'Södermanland',
    center: [16.5448, 59.2753], // Eskilstuna
    zoom: 10,
    hasRealtime: false,
    hasVehiclePositions: false
  }
};

// Filter operators that have vehicle position tracking
export const OPERATORS_WITH_TRACKING = Object.values(OPERATORS).filter(
  op => op.hasVehiclePositions
);

// Polling interval presets (in milliseconds)
export const POLL_INTERVALS = {
  REALTIME: 10000,      // 10 seconds - ~259,200 req/month (exceeds Bronze limit!)
  FREQUENT: 30000,      // 30 seconds - ~86,400 req/month (exceeds Bronze limit!)
  NORMAL: 60000,        // 1 minute - ~43,200 req/month (safe for Bronze: 30k/month)
  MODERATE: 120000,     // 2 minutes - ~21,600 req/month (safe)
  CONSERVATIVE: 180000, // 3 minutes - ~14,400 req/month (safe)
  MINIMAL: 300000       // 5 minutes - ~8,640 req/month (very safe)
};

export const RATE_LIMIT_INFO = {
  BRONZE: {
    name: 'Bronze (Free)',
    limit: 30000,
    period: '30 days',
    recommended: POLL_INTERVALS.NORMAL, // 1 minute
    description: 'Up to 30,000 requests per 30 days'
  },
  SILVER: {
    name: 'Silver',
    limit: 250000,
    period: '30 days',
    recommended: POLL_INTERVALS.FREQUENT, // 30 seconds
    description: 'Up to 250,000 requests per 30 days'
  },
  GOLD: {
    name: 'Gold',
    limit: 500000,
    period: '30 days',
    recommended: POLL_INTERVALS.REALTIME, // 10 seconds
    description: 'Up to 500,000 requests per 30 days'
  }
};

// LocalStorage keys
export const STORAGE_KEYS = {
  REALTIME_API_KEY: 'trafiklab_realtime_api_key',
  STATIC_API_KEY: 'trafiklab_static_api_key',
  POLL_INTERVAL: 'poll_interval',
  OPERATOR: 'selected_operator',
  // Legacy key for migration
  API_KEY: 'trafiklab_api_key'
};

// Get selected operator from localStorage or return default
export function getSelectedOperator(): string {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(STORAGE_KEYS.OPERATOR) || 'sl';
  }
  return 'sl';
}

// Set selected operator in localStorage
export function setSelectedOperator(operatorId: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEYS.OPERATOR, operatorId);
  }
}

// Get realtime API key from localStorage or return empty string
export function getRealtimeApiKey(): string {
  if (typeof window !== 'undefined') {
    // Try new key first, fallback to legacy key
    return localStorage.getItem(STORAGE_KEYS.REALTIME_API_KEY)
      || localStorage.getItem(STORAGE_KEYS.API_KEY)
      || '';
  }
  return '';
}

// Set realtime API key in localStorage
export function setRealtimeApiKey(key: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEYS.REALTIME_API_KEY, key);
    // Clean up legacy key
    localStorage.removeItem(STORAGE_KEYS.API_KEY);
  }
}

// Legacy function for backward compatibility
export function getApiKey(): string {
  return getRealtimeApiKey();
}

// Legacy function for backward compatibility
export function setApiKey(key: string): void {
  setRealtimeApiKey(key);
}

// Get poll interval from localStorage or return default
export function getPollInterval(): number {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEYS.POLL_INTERVAL);
    if (stored) {
      return parseInt(stored, 10);
    }
  }
  return POLL_INTERVALS.NORMAL; // Default to 1 minute (safe for Bronze)
}

// Set poll interval in localStorage
export function setPollInterval(interval: number): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEYS.POLL_INTERVAL, interval.toString());
  }
}

export const API_CONFIG = {
  // API endpoints
  BASE_URL: 'https://opendata.samtrafiken.se',

  // Required headers for API requests
  HEADERS: {
    'Accept': 'application/x-protobuf',
    'Accept-Encoding': 'gzip'
  }
};

// Default operator (can be changed by user)
const DEFAULT_OPERATOR = OPERATORS.sl;

export const MAP_CONFIG = {
  STYLE: 'https://basemaps.cartocdn.com/gl/dark-matter-nolabels-gl-style/style.json',
  INITIAL_VIEW_STATE: {
    longitude: DEFAULT_OPERATOR.center[0],
    latitude: DEFAULT_OPERATOR.center[1],
    zoom: DEFAULT_OPERATOR.zoom,
    pitch: 45,
    bearing: 0
  }
};
